import { ref, onMounted } from "vue";
import { DOM_SELECTORS } from "@/viewer/constants.js";

/**
 * Vue Composable: DOM Observation Management
 *
 * This composable manages DOM observation for dynamic elements that appear
 * after the initial component mount. It's specifically designed to watch for
 * the Glycerine viewer's overlay elements that are needed for Vue's Teleport feature.
 *
 * Use Case:
 * The Glycerine viewer creates overlay elements dynamically when entering modal
 * mode. Vue's Teleport component needs to know when these target elements are
 * available before it can teleport content (like close buttons) into them.
 *
 * Features:
 * - Reactive target availability tracking
 * - Automatic observer cleanup after target found
 * - Component lifecycle integration
 * - State reset for multiple modal sessions
 *
 * @returns {Object} Target state and observer management functions
 */
export function useDOMObserver() {
    // REACTIVE STATE

    /**
     * Tracks whether the teleport target element is available
     *
     * This boolean flag indicates when the viewer's overlay element is present
     * in the DOM and ready to receive teleported content. Used by Vue's Teleport
     * component to conditionally render the close button.
     *
     * @type {import('vue').Ref<boolean>}
     */
    const targetReady = ref(false);

    // DOM OBSERVATION METHODS

    /**
     * Create and start a DOM observer for the teleport target
     *
     * Uses MutationObserver to watch for the appearance of the viewer's overlay
     * element. Once found, updates the reactive state and automatically disconnects
     * the observer to prevent unnecessary ongoing observations.
     *
     * Observer Configuration:
     * - childList: Watch for addition/removal of child elements
     * - subtree: Watch the entire document body tree
     * - Target: div.absolute element created by Glycerine viewer
     *
     * @returns {MutationObserver} The created observer instance
     */
    function observeDOM() {
        const observer = new MutationObserver(() => {
            if (document.querySelector(DOM_SELECTORS.ABSOLUTE_DIV)) {
                targetReady.value = true;
                observer.disconnect(); // Stop observing once target is found
            }
        });

        observer.observe(document.body, {
            childList: true, // Watch for child element changes
            subtree: true, // Watch entire subtree for deep changes
        });

        return observer;
    }

    /**
     * Reset the target ready state
     *
     * Called when transitioning back to preview mode to reset the state
     * for the next modal session. This ensures the observer will properly
     * detect the target element when the modal is opened again.
     */
    function resetTargetReady() {
        targetReady.value = false;
    }

    // LIFECYCLE INTEGRATION

    /**
     * Initialize DOM observation on component mount
     *
     * Integrates the DOM observer with Vue's component lifecycle by starting
     * observation when the component mounts. This ensures the observer is
     * ready to detect target elements as soon as the component is active.
     */
    function initializeDOMObserver() {
        onMounted(() => {
            observeDOM();
        });
    }

    // PUBLIC API

    return {
        // Reactive State
        targetReady,

        // Observer Management
        observeDOM,
        resetTargetReady,

        // Lifecycle Integration
        initializeDOMObserver,
    };
}
